import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { AlertTriangle, Check, Library, Settings } from 'lucide-react';
import { CostUnit } from '../../../../TemplatesEditor/types';

export interface TemplateCostLibraryPickerProps {
    open: boolean;
    onClose: () => void;
    /** Fill the form with a library entry's cost and unit. Nothing is saved until the user submits. */
    onPick: (cost: number, costUnit: CostUnit) => void;
}

interface TemplateCostFormModalProps {
    title: string;
    warningMessage: React.ReactNode;
    costLabel: string;
    currentCost: number;
    currentCostUnit: CostUnit;
    /**
     * Renders the library picker to fetch the cost from. The caller chooses the
     * library; without it the "Hämta kostnad från bibliotek" button is not shown.
     */
    renderLibraryPicker?: (props: TemplateCostLibraryPickerProps) => React.ReactNode;
    onSave: (cost: number, costUnit: CostUnit) => Promise<void>;
    onClose: () => void;
}

/**
 * Shared modal for editing template costs (both lifetime and activity templates).
 * Displays a warning that changes affect all components using this template.
 *
 * Uses react-bootstrap Modal so the library picker stacks on top of it.
 */
export const TemplateCostFormModal: React.FC<TemplateCostFormModalProps> = ({
    title,
    warningMessage,
    costLabel,
    currentCost,
    currentCostUnit,
    renderLibraryPicker,
    onSave,
    onClose
}) => {
    const [cost, setCost] = useState<string>(currentCost.toString());
    const [costUnit, setCostUnit] = useState<CostUnit>(currentCostUnit);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showLibraryPicker, setShowLibraryPicker] = useState<boolean>(false);

    const handleLibraryPick = (pickedCost: number, pickedCostUnit: CostUnit): void => {
        setCost(pickedCost.toString());
        setCostUnit(pickedCostUnit);
        setError(null);
        setShowLibraryPicker(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate cost
        const costValue = parseFloat(cost);
        if (isNaN(costValue) || costValue < 0) {
            setError('Kostnad får inte vara negativ');
            return;
        }
        if (costValue > 100000000) {
            setError('Kostnad är för hög');
            return;
        }

        setSaving(true);
        try {
            await onSave(costValue, costUnit);
            onClose();
        } catch (err) {
            console.error('Error saving template cost:', err);
            setError('Ett fel uppstod vid sparande. Försök igen.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Modal show onHide={onClose} centered>
                <Modal.Header closeButton={!saving} closeLabel="Stäng">
                    <Modal.Title as="h5">
                        <Settings size={20} className="me-2" />
                        {title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        {/* Warning about implications */}
                        <div className="alert alert-warning d-flex align-items-start mb-4">
                            <AlertTriangle size={20} className="me-2 flex-shrink-0 mt-1" />
                            <div>
                                {warningMessage}
                            </div>
                        </div>

                        {/* Cost Input */}
                        <div className="mb-3">
                            <label htmlFor="templateCost" className="form-label">
                                {costLabel} <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="form-control"
                                    id="templateCost"
                                    value={cost}
                                    onChange={(e) => setCost(e.target.value)}
                                    min="0"
                                    step="1"
                                    required
                                    disabled={saving}
                                />
                                <span className="input-group-text">kr</span>
                            </div>
                            {renderLibraryPicker && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm mt-2"
                                    onClick={() => setShowLibraryPicker(true)}
                                    disabled={saving}
                                >
                                    <Library size={16} className="me-2" />
                                    Hämta kostnad från bibliotek
                                </button>
                            )}
                        </div>

                        {/* Cost Unit Selection */}
                        <div className="mb-4">
                            <label className="form-label">Kostnadsenhet</label>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="costUnit"
                                    id="costUnitPerUnit"
                                    value="per_unit"
                                    checked={costUnit === 'per_unit'}
                                    onChange={() => setCostUnit('per_unit')}
                                    disabled={saving}
                                />
                                <label className="form-check-label" htmlFor="costUnitPerUnit">
                                    Per styck (fast belopp)
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="costUnit"
                                    id="costUnitPerSqm"
                                    value="per_sqm"
                                    checked={costUnit === 'per_sqm'}
                                    onChange={() => setCostUnit('per_sqm')}
                                    disabled={saving}
                                />
                                <label className="form-check-label" htmlFor="costUnitPerSqm">
                                    Per kvadratmeter (multipliceras med komponentens area)
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="costUnit"
                                    id="costUnitPerLm"
                                    value="per_lm"
                                    checked={costUnit === 'per_lm'}
                                    onChange={() => setCostUnit('per_lm')}
                                    disabled={saving}
                                />
                                <label className="form-check-label" htmlFor="costUnitPerLm">
                                    Per löpmeter (multipliceras med komponentens längd)
                                </label>
                            </div>
                        </div>

                        <div className="modal-footer px-0 pb-0">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={saving}
                            >
                                Avbryt
                            </button>
                            <button
                                type="submit"
                                className="btn btn-warning"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Sparar...
                                    </>
                                ) : (
                                    <>
                                        <Check size={16} className="me-2" />
                                        Spara ändringar
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
            {renderLibraryPicker?.({
                open: showLibraryPicker,
                onClose: () => setShowLibraryPicker(false),
                onPick: handleLibraryPick,
            })}
        </>
    );
};
